import { AudioSystemEvent } from './AudioSystem';
import AudioClip, { AudioTrackType, IAudioParams } from './AudioClip';
import Game from 'Game';
import { AudioSpriteMap } from './AudioSpriteMap';
import { audioSceneId } from 'State/Game/GameScene';
import Scene from 'Scene/Scene';

export const AUDIO_PATH = 'resource/audio/';

/**
 * Start playing music. This will try to start the specific audio clip in the AudioScene. Music is played in the
 * AudioScene to allow for cross-fading music between scenes.
 * @param clipName
 * @param params
 * @constructor
 */
export function PlayMusic(clipName: string, params?: Partial<IAudioParams>) {
    const audioScene = Game.scene.getScene(audioSceneId);
    StartAudio(audioScene, clipName, AudioTrackType.MUSIC, {
        loop: true,
        ...params,
    });
}

/**
 * Start the specified audio clip in the currently active scene. Note this method assumes the associated audio is
 * already loaded - it will not wait for the asset to load if it is queued.
 * @param clipName
 * @param params
 * @constructor
 */
export function PlaySound(clipName: string, params?: Partial<IAudioParams>) {
    const activeScene = Game.scene.getActiveScene();
    StartAudio(activeScene, clipName, AudioTrackType.SFX, params);
}

/**
 * Stop the specified clip in the active scene.
 * @param clipName
 * @constructor
 */
export function StopSound(clipName: string) {
    const activeScene = Game.scene.getActiveScene();
    StopAudio(activeScene, clipName);
}

/**
 * Stop the specified clip in the audio scene.
 * @param clipName
 * @constructor
 */
export function StopMusic(clipName: AudioClip | string) {
    const audioScene = Game.scene.getScene(audioSceneId);
    StopAudio(audioScene, clipName);
}

export function SetMusicVolume(clip: AudioClip | string, volume: number) {
    const audioScene = Game.scene.getScene(audioSceneId);
    SetClipVolume(audioScene, clip, volume);
}

/**
 * Try to start the specified audio scene in the specified scene.
 * @param scene
 * @param clipName
 * @param type
 * @param params
 * @constructor
 */
function StartAudio(scene: Scene, clipName: string, type: AudioTrackType, params?: Partial<IAudioParams>) {
    if (!scene?.active) return;
    const audioClip = new AudioClip(clipName, type, params);
    scene.emit(AudioSystemEvent.PLAY, audioClip);
}

function StopAudio(scene: Scene, clip: AudioClip | string): void {
    scene.emit(AudioSystemEvent.STOP, clip);
}

function SetClipVolume(scene: Scene, clip: AudioClip | string, volume: number): void {
    scene.emit(AudioSystemEvent.VOLUME, clip, volume);
}

export function loadAudio(audioPath: string): string {
    Game.resource.add(audioPath, `${AUDIO_PATH}${audioPath}`);
    return audioPath;
}

/**
 * Get the key of the audio sprite that contains the provided clip name, if any.
 * Defaults to undefined if no sprite is found
 */
export function getAudioSpriteKey(clipName: string): string {
    for (const key in AudioSpriteMap) {
        if (AudioSpriteMap[key].includes(clipName)) {
            return key;
        }
    }
    return undefined;
}
